import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";

const CATS = ["Brennholz", "Bauholz", "Forstdienstleistungen", "Holzkunst"];
const EMPTY = { name: "", category: "Brennholz", price: "", unit: "€/SRM", note: "", sort: 0 };

export const PricesAdmin = () => {
    const [prices, setPrices] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = useCallback(() => {
        api.get("/prices").then((r) => setPrices(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const openDialog = (p) => {
        setEditing(p || null);
        setForm(p ? { ...p, price: p.price ?? "", note: p.note || "" } : EMPTY);
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, price: form.price === "" ? null : Number(form.price), sort: Number(form.sort) || 0 };
        try {
            if (editing) await api.put(`/admin/prices/${editing.id}`, payload);
            else await api.post("/admin/prices", payload);
            toast.success("Preis gespeichert.");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (p) => {
        try {
            await api.delete(`/admin/prices/${p.id}`);
            toast.success(`„${p.name}" gelöscht.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-prices">
            <div className="mb-6 flex justify-end">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button data-testid="admin-price-add" onClick={() => openDialog(null)} className="flex items-center gap-2 rounded-full bg-[#C88A45] px-5 py-2 text-sm font-semibold text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057]">
                            <Plus size={15} /> Neuer Eintrag
                        </button>
                    </DialogTrigger>
                    <DialogContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl">{editing ? "Eintrag bearbeiten" : "Neuer Preiseintrag"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={save} className="space-y-4">
                            <Input data-testid="price-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name, z. B. Buche" className="border-[#2E2517] bg-[#120E09]" />
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="price-form-category" className="border-[#2E2517] bg-[#120E09]"><SelectValue /></SelectTrigger>
                                <SelectContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                    {CATS.map((c) => <SelectItem key={c} value={c} data-testid={`price-form-cat-${c.toLowerCase()}`}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="grid grid-cols-2 gap-4">
                                <Input data-testid="price-form-price" type="number" step="0.5" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Preis (leer = auf Anfrage)" className="border-[#2E2517] bg-[#120E09]" />
                                <Input data-testid="price-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Einheit, z. B. €/SRM" className="border-[#2E2517] bg-[#120E09]" />
                            </div>
                            <Input data-testid="price-form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Notiz (optional)" className="border-[#2E2517] bg-[#120E09]" />
                            <Input data-testid="price-form-sort" type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: e.target.value })} placeholder="Reihenfolge (0 = oben)" className="border-[#2E2517] bg-[#120E09]" />
                            <button data-testid="price-form-save" type="submit" className="w-full rounded-full bg-[#C88A45] py-2.5 text-sm font-semibold text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057]">
                                Speichern
                            </button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="overflow-x-auto rounded border border-[#2E2517] bg-[#1B150E]">
                <Table>
                    <TableHeader>
                        <TableRow className="border-[#2E2517]">
                            <TableHead className="text-[#9C917D]">Name</TableHead>
                            <TableHead className="text-[#9C917D]">Kategorie</TableHead>
                            <TableHead className="text-[#9C917D]">Preis</TableHead>
                            <TableHead className="text-[#9C917D]">Notiz</TableHead>
                            <TableHead className="text-right text-[#9C917D]">Aktionen</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {prices.map((p) => (
                            <TableRow key={p.id} className="border-[#2E2517]" data-testid={`admin-price-row-${p.id}`}>
                                <TableCell className="text-[#EDE6DA]">{p.name}</TableCell>
                                <TableCell className="text-[#9C917D]">{p.category}</TableCell>
                                <TableCell className="text-[#C88A45]">{p.price != null ? `ab ${p.price} ${p.unit}` : p.unit}</TableCell>
                                <TableCell className="max-w-[220px] truncate text-[#9C917D]">{p.note || "—"}</TableCell>
                                <TableCell className="text-right">
                                    <button data-testid={`admin-price-edit-${p.id}`} onClick={() => openDialog(p)} aria-label="Bearbeiten" className="mr-3 text-[#9C917D] transition-colors hover:text-[#C88A45]"><Pencil size={15} /></button>
                                    <button data-testid={`admin-price-delete-${p.id}`} onClick={() => remove(p)} aria-label="Löschen" className="text-[#9C917D] transition-colors hover:text-red-400"><Trash2 size={15} /></button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};
